import React, { useState } from 'react';
import { addDoc, collection, serverTimestamp, getDocs, query, where } from 'firebase/firestore';
import { firestore } from '../admin/FirebaseConfig';

const Refund = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    applicationNo: '',
    amount: ''
  });

  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
    // Clear error when user starts typing
    if (formErrors[e.target.id]) {
      setFormErrors({ ...formErrors, [e.target.id]: '' });
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    if (!formData.mobile.trim()) {
      errors.mobile = 'Mobile number is required';
    } else if (!/^[0-9]{10}$/.test(formData.mobile)) {
      errors.mobile = 'Please enter a valid 10-digit mobile number';
    }
    if (!formData.applicationNo.trim()) errors.applicationNo = 'Application number is required';
    if (!formData.amount.trim()) {
      errors.amount = 'Amount is required';
    } else if (isNaN(formData.amount) || parseFloat(formData.amount) <= 0) {
      errors.amount = 'Please enter a valid amount';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const checkApplicationExists = async (applicationNo) => {
    const loanRef = collection(firestore, 'loanApplications');
    const q = query(loanRef, where('applicationNumber', '==', applicationNo));
    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitSuccess(false);

    try {
      const exists = await checkApplicationExists(formData.applicationNo);
      if (!exists) {
        setFormErrors((prev) => ({
          ...prev,
          applicationNo: 'Invalid Application Number. No record found.',
        }));
        setIsSubmitting(false);
        return;
      }

      await addDoc(collection(firestore, 'Refund'), {
        ...formData,
        amount: parseFloat(formData.amount),
        timestamp: serverTimestamp(),
      });

      setSubmitSuccess(true);
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        mobile: '',
        applicationNo: '',
        amount: ''
      });
      setFormErrors({});
      
      // Auto hide success message after 5 seconds
      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);
      
    } catch (error) {
      console.error('Error submitting refund:', error);
      alert('There was an error submitting the refund form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container-fluid min-vh-100 py-5" style={{ 
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 50%, #f8f9fa 100%)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Wireframe Background with Circles */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: `
          radial-gradient(circle, rgba(139, 0, 0, 0.05) 2px, transparent 2px),
          radial-gradient(circle, rgba(139, 0, 0, 0.05) 1px, transparent 1px)
        `,
        backgroundSize: '40px 40px, 80px 80px',
        backgroundPosition: '0 0, 20px 20px',
        zIndex: 0
      }}></div>
      
      {/* Large decorative circles */}
      <div style={{
        position: 'absolute',
        width: '300px',
        height: '300px',
        borderRadius: '50%',
        border: '2px dashed rgba(139, 0, 0, 0.1)',
        top: '-100px',
        right: '-100px',
        zIndex: 0
      }}></div>
      
      <div style={{
        position: 'absolute',
        width: '500px',
        height: '500px',
        borderRadius: '50%',
        border: '2px dashed rgba(139, 0, 0, 0.08)',
        bottom: '-250px',
        left: '-250px',
        zIndex: 0
      }}></div>

      <div className="container position-relative" style={{ zIndex: 1 }}>
        {/* Header Section */}
        <div className="text-center mb-5">
          <h1 className="display-5 fw-bold mb-3" style={{ 
            color: '#8B0000',
            background: 'linear-gradient(135deg, #8B0000 0%, #6d0000 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Apply for Refund
          </h1>
          <p className="lead text-muted mx-auto" style={{ maxWidth: '600px' }}>
            Request a refund for your payment. We'll process your request and get back to you within 3-5 business days.
          </p>
          <div className="mx-auto" style={{ 
            height: '4px', 
            width: '80px', 
            background: 'linear-gradient(to right, #8B0000, #ffd700, #8B0000)',
            borderRadius: '2px'
          }}></div>
        </div>

        <div className="row justify-content-center">
          <div className="col-12 col-lg-10">
            <div className="row gy-4">
              {/* Image Column - Made same height as form */}
              <div className="col-12 col-md-5 d-flex align-items-stretch">
                <div className="position-relative w-100" style={{ 
                  borderRadius: '20px', 
                  overflow: 'hidden',
                  boxShadow: '0 15px 30px rgba(0,0,0,0.1)',
                  height: '100%',
                  minHeight: '400px'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(45deg, #8B0000 0%, #6d0000 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '24px',
                    fontWeight: 'bold'
                  }}>
                    <div className="text-center p-4">
                      <i className="fas fa-money-bill-wave mb-3" style={{ fontSize: '48px' }}></i>
                      <h3>Refund Process</h3>
                      <p className="mt-3">Quick & Hassle-free Refunds</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Form Column - Made same height as image */}
              <div className="col-12 col-md-7 d-flex align-items-stretch">
                <div className="card border-0 shadow-lg w-100" style={{ 
                  borderRadius: '20px',
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                  overflow: 'hidden'
                }}>
                  <div className="card-header border-0 py-4" style={{
                    background: 'linear-gradient(135deg, #8B0000 0%, #6d0000 100%)'
                  }}>
                    <h3 className="text-white text-center mb-0">
                      <i className="fas fa-money-bill-wave me-2"></i>
                      Refund Request Form
                    </h3>
                  </div>
                  
                  <div className="card-body p-4 p-md-5">
                    {submitSuccess && (
                      <div className="alert alert-success d-flex align-items-center" role="alert" style={{ borderRadius: '10px' }}>
                        <i className="fas fa-check-circle me-2 fs-4"></i>
                        <div>
                          <h6 className="mb-1">Refund application submitted successfully!</h6>
                          <p className="mb-0 small">We will contact you soon regarding your refund.</p>
                        </div>
                      </div>
                    )}
                    
                    <form onSubmit={handleSubmit}>
                      <div className="mb-4">
                        <label htmlFor="name" className="form-label fw-semibold">Full Name</label>
                        <div className="input-group">
                          <span className="input-group-text bg-transparent border-end-0">
                            <i className="fas fa-user" style={{ color: '#8B0000' }}></i>
                          </span>
                          <input
                            type="text"
                            className={`form-control py-3 border-start-0 ${formErrors.name ? 'is-invalid' : ''}`}
                            id="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Enter your full name"
                            style={{ borderRadius: '0 8px 8px 0' }}
                          />
                        </div>
                        {formErrors.name && <div className="invalid-feedback d-block mt-1">{formErrors.name}</div>}
                      </div>

                      <div className="row mb-4">
                        <div className="col-12 col-md-6 mb-3 mb-md-0">
                          <label htmlFor="email" className="form-label fw-semibold">Email Address</label>
                          <div className="input-group">
                            <span className="input-group-text bg-transparent border-end-0">
                              <i className="fas fa-envelope" style={{ color: '#8B0000' }}></i>
                            </span>
                            <input
                              type="email"
                              className={`form-control py-3 border-start-0 ${formErrors.email ? 'is-invalid' : ''}`}
                              id="email"
                              value={formData.email}
                              onChange={handleChange}
                              placeholder="support@adityabirlafinanceloan.com"
                              style={{ borderRadius: '0 8px 8px 0' }}
                            />
                          </div>
                          {formErrors.email && <div className="invalid-feedback d-block mt-1">{formErrors.email}</div>}
                        </div>
                        
                        <div className="col-12 col-md-6">
                          <label htmlFor="mobile" className="form-label fw-semibold">Mobile Number</label>
                          <div className="input-group">
                            <span className="input-group-text bg-transparent border-end-0">
                              <i className="fas fa-phone" style={{ color: '#8B0000' }}></i>
                            </span>
                            <input
                              type="text"
                              className={`form-control py-3 border-start-0 ${formErrors.mobile ? 'is-invalid' : ''}`}
                              id="mobile"
                              value={formData.mobile}
                              onChange={handleChange}
                              placeholder="10-digit number"
                              maxLength="10"
                              style={{ borderRadius: '0 8px 8px 0' }}
                            />
                          </div>
                          {formErrors.mobile && <div className="invalid-feedback d-block mt-1">{formErrors.mobile}</div>}
                        </div>
                      </div>

                      <div className="row mb-4">
                        <div className="col-12 col-md-6 mb-3 mb-md-0">
                          <label htmlFor="applicationNo" className="form-label fw-semibold">Application Number</label>
                          <div className="input-group">
                            <span className="input-group-text bg-transparent border-end-0">
                              <i className="fas fa-file-alt" style={{ color: '#8B0000' }}></i>
                            </span>
                            <input
                              type="text"
                              className={`form-control py-3 border-start-0 ${formErrors.applicationNo ? 'is-invalid' : ''}`}
                              id="applicationNo"
                              value={formData.applicationNo}
                              onChange={handleChange}
                              placeholder="App-12345"
                              style={{ borderRadius: '0 8px 8px 0' }}
                            />
                          </div>
                          {formErrors.applicationNo && <div className="invalid-feedback d-block mt-1">{formErrors.applicationNo}</div>}
                        </div>
                        
                        <div className="col-12 col-md-6">
                          <label htmlFor="amount" className="form-label fw-semibold">Refund Amount (₹)</label>
                          <div className="input-group">
                            <span className="input-group-text bg-transparent border-end-0">
                              <i className="fas fa-rupee-sign" style={{ color: '#8B0000' }}></i>
                            </span>
                            <input
                              type="text"
                              className={`form-control py-3 border-start-0 ${formErrors.amount ? 'is-invalid' : ''}`}
                              id="amount"
                              value={formData.amount}
                              onChange={handleChange}
                              placeholder="0.00"
                              style={{ borderRadius: '0 8px 8px 0' }}
                            />
                          </div>
                          {formErrors.amount && <div className="invalid-feedback d-block mt-1">{formErrors.amount}</div>}
                        </div>
                      </div>

                      <button 
                        type="submit" 
                        className="btn w-100 py-3 fw-bold mt-2"
                        disabled={isSubmitting}
                        style={{
                          background: 'linear-gradient(135deg, #8B0000 0%, #6d0000 100%)',
                          color: 'white',
                          borderRadius: '10px',
                          border: 'none',
                          transition: 'all 0.3s',
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                        onMouseOver={(e) => {
                          e.target.style.background = 'linear-gradient(135deg, #6d0000 0%, #8B0000 100%)';
                          e.target.style.transform = 'translateY(-2px)';
                        }}
                        onMouseOut={(e) => {
                          e.target.style.background = 'linear-gradient(135deg, #8B0000 0%, #6d0000 100%)';
                          e.target.style.transform = 'translateY(0)';
                        }}
                      >
                        {isSubmitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Processing Request...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-paper-plane me-2"></i>
                            Submit Refund Request
                          </>
                        )}
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="row mt-5">
          <div className="col-12">
            <div className="p-4 rounded-4 shadow-sm" style={{ 
              backgroundColor: 'rgba(139, 0, 0, 0.03)', 
              borderLeft: '4px solid #8B0000' 
            }}>
              <h5 className="mb-3" style={{ color: '#8B0000' }}>
                <i className="fas fa-info-circle me-2"></i>
                Refund Process Information
              </h5>
              <div className="row">
                <div className="col-md-4 mb-3">
                  <div className="d-flex">
                    <div className="me-3">
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        backgroundColor: 'rgba(139, 0, 0, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <span className="fw-bold" style={{ color: '#8B0000' }}>1</span>
                      </div>
                    </div>
                    <div>
                      <h6 className="mb-1">Submit Request</h6>
                      <p className="small text-muted mb-0">Fill out the refund request form with accurate details</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-4 mb-3">
                  <div className="d-flex">
                    <div className="me-3">
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        backgroundColor: 'rgba(139, 0, 0, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <span className="fw-bold" style={{ color: '#8B0000' }}>2</span>
                      </div>
                    </div>
                    <div>
                      <h6 className="mb-1">Verification</h6>
                      <p className="small text-muted mb-0">We verify your application and payment details</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-4 mb-3">
                  <div className="d-flex">
                    <div className="me-3">
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        backgroundColor: 'rgba(139, 0, 0, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <span className="fw-bold" style={{ color: '#8B0000' }}>3</span>
                      </div>
                    </div>
                    <div>
                      <h6 className="mb-1">Processing</h6>
                      <p className="small text-muted mb-0">Refund processed within 3-5 business days</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Font Awesome for icons */}
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css" />
    </div>
  );
};

export default Refund;